import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Box,
  Alert
} from '@mui/material';
import { tagsApi } from '../services/api';
import TagSelector from './TagSelector';
import LoadingButton from './LoadingButton';
import { categoryMenuItems } from '../utils/categoryMenu';

const BulkEditModal = ({ open, onClose, onSave, selectedCount, categories = [], loading = false }) => {
  const [formData, setFormData] = useState({
    accountId: '',
    category: '',
    accountType: '',
    bank: '',
    user: '',
    tagMode: '', // 'add', 'replace', 'remove', or ''
    tagIds: []
  });
  const [allTags, setAllTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);

  useEffect(() => {
    if (open) {
      loadTags();
    }
  }, [open]);

  const loadTags = async () => {
    try {
      const response = await tagsApi.getAll();
      setAllTags(response.data.tags || []);
    } catch (error) {
      console.error('Error loading tags:', error);
      setAllTags([]);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = () => {
    // Only send fields that have values
    const updates = {};
    Object.keys(formData).forEach(key => {
      // Skip tag-related fields for now
      if (key === 'tagMode' || key === 'tagIds') return;
      
      if (formData[key] && formData[key].trim() !== '') {
        updates[key] = formData[key].trim();
      }
    });

    // Handle tags if a tag mode is selected
    if (formData.tagMode && formData.tagMode !== '') {
      updates.tagMode = formData.tagMode;
      updates.tagIds = selectedTags.map(tag => tag.id);
    }

    if (Object.keys(updates).length === 0) {
      // No fields to update
      return;
    }

    onSave(updates);
    handleClose();
  };

  const handleClose = () => {
    // Reset form
    setFormData({
      accountId: '',
      category: '',
      accountType: '',
      bank: '',
      user: '',
      tagMode: '',
      tagIds: []
    });
    setSelectedTags([]);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Bulk Edit - {selectedCount} Transaction{selectedCount !== 1 ? 's' : ''}
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Update the following fields for {selectedCount} selected transaction{selectedCount !== 1 ? 's' : ''}
          </Typography>

          {/* Account ID */}
          <TextField
            fullWidth
            label="Account ID (Optional)"
            value={formData.accountId}
            onChange={(e) => handleChange('accountId', e.target.value)}
            helperText="Last 4 digits of account (e.g., 0123)"
            sx={{ mb: 2 }}
          />

          {/* Category */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Category (Optional)</InputLabel>
            <Select
              value={formData.category}
              onChange={(e) => handleChange('category', e.target.value)}
              label="Category (Optional)"
            >
              <MenuItem value="">
                <em>Don't change</em>
              </MenuItem>
              {categoryMenuItems(categories)}
            </Select>
          </FormControl>

          {/* Account Type */}
          <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Account Type (Optional)
            </Typography>
            <RadioGroup
              row
              value={formData.accountType}
              onChange={(e) => handleChange('accountType', e.target.value)}
            >
              <FormControlLabel 
                value="" 
                control={<Radio />} 
                label="Don't change" 
              />
              <FormControlLabel 
                value="bank_account" 
                control={<Radio />} 
                label="Bank Account" 
              />
              <FormControlLabel 
                value="credit_card" 
                control={<Radio />} 
                label="Credit Card" 
              />
            </RadioGroup>
          </FormControl>

          {/* Bank */}
          <TextField
            fullWidth
            label="Bank (Optional)"
            value={formData.bank}
            onChange={(e) => handleChange('bank', e.target.value)}
            helperText="Bank name (e.g., HDFC, SBI, Chase, Wells Fargo)"
            sx={{ mb: 2 }}
          />

          {/* User (for joint accounts) */}
          <TextField
            fullWidth
            label="User (Optional)"
            value={formData.user}
            onChange={(e) => handleChange('user', e.target.value)}
            helperText="For joint accounts - cardholder name"
            sx={{ mb: 2 }}
          />

          {/* Tags Section */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
              Tags (Optional)
            </Typography>
            
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Tag Operation</InputLabel>
              <Select
                value={formData.tagMode}
                onChange={(e) => handleChange('tagMode', e.target.value)}
                label="Tag Operation"
              >
                <MenuItem value="">
                  <em>Don't change tags</em>
                </MenuItem>
                <MenuItem value="add">Add tags (keep existing)</MenuItem>
                <MenuItem value="replace">Replace all tags</MenuItem>
                <MenuItem value="remove">Remove tags</MenuItem>
              </Select>
            </FormControl>

            {formData.tagMode && formData.tagMode !== '' && (
              <TagSelector
                value={selectedTags}
                onChange={setSelectedTags}
                allTags={allTags}
                label={
                  formData.tagMode === 'add' ? 'Tags to Add' :
                  formData.tagMode === 'replace' ? 'New Tags' :
                  'Tags to Remove'
                }
                placeholder={
                  formData.tagMode === 'add' ? 'Select tags to add...' :
                  formData.tagMode === 'replace' ? 'Select new tags...' :
                  'Select tags to remove...'
                }
              />
            )}
          </Box>

          <Alert severity="info" sx={{ mt: 2 }}>
            💡 Fields left empty will not be updated. Existing values will be preserved.
          </Alert>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="inherit" disabled={loading}>
          Cancel
        </Button>
        <LoadingButton
          onClick={handleSave} 
          variant="contained" 
          color="primary"
          loading={loading}
        >
          Apply to {selectedCount} item{selectedCount !== 1 ? 's' : ''}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default BulkEditModal;

